import { useEffect, useMemo, useState } from 'react';
import Chart from 'react-apexcharts';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { ApexOptions } from 'apexcharts';
import { TFunction } from 'i18next';

import { TransactionsRoutes } from 'support/types';
import { TransactionGraphData } from 'support/types/dtos';
import { Badge, BadgeColor } from 'components/Display/Badge/Badge';
import { Button } from 'components/Form/Button/Button';

import { convertToSeries, getAllDates } from '../../helpers';
import { DateRange, TransactionSeries } from '../../types';

const SERIES_DEFINITIONS: Array<{
  key: keyof TransactionGraphData;
  color: string;
  badgeColor: BadgeColor;
  nameKey: string;
  labelKey: string;
}> = [
  {
    key: 'orders',
    color: '#34D399',
    badgeColor: BadgeColor.green,
    nameKey: 'dashboard:orders',
    labelKey: 'dashboard:transactions.orders',
  },
  {
    key: 'shippingNotices',
    color: '#A78BFA',
    badgeColor: BadgeColor.indigo,
    nameKey: 'dashboard:shippingNotices',
    labelKey: 'dashboard:transactions.shippingNotices',
  },
  {
    key: 'invoices',
    color: '#60A5FA',
    badgeColor: BadgeColor.blue,
    nameKey: 'dashboard:transactions.invoices',
    labelKey: 'dashboard:transactions.invoices',
  },
  {
    key: 'dispatchInstructions',
    color: '#34D399',
    badgeColor: BadgeColor.green,
    nameKey: 'dashboard:transactions.dispatchInstructions',
    labelKey: 'dashboard:transactions.dispatchInstructions',
  },
  {
    key: 'dispatchInstructionResponses',
    color: '#60A5FA',
    badgeColor: BadgeColor.blue,
    nameKey: 'dashboard:transactions.dispatchInstructionResponses',
    labelKey: 'dashboard:transactions.dispatchInstructionResponses',
  },
];

const chartOptions: ApexOptions = {
  chart: {
    fontFamily: 'Inter, Helvetica, Arial, sans-serif',
    toolbar: {
      show: false,
    },
    animations: {
      enabled: false,
    },
  },
  dataLabels: {
    enabled: false,
  },
  stroke: {
    curve: 'straight',
    width: 2,
  },
  fill: {
    opacity: 0.2,
    type: 'solid',
  },
  legend: {
    show: false,
  },
  xaxis: {
    type: 'datetime',
  },
  yaxis: {
    show: false,
  },
};

type TransactionGraphProps = {
  dateRangeOptions: Array<DateRange>;
  transactions: TransactionGraphData | undefined;
  chartDateRange: number;
  loading: boolean;
};

type DefaultSeriesProps = {
  transactions?: TransactionGraphData;
  dateRange: number;
  dates: Array<number>;
  t: TFunction;
};
export function TransactionGraph({ transactions, dateRangeOptions, chartDateRange }: TransactionGraphProps) {
  const { t } = useTranslation();
  const zoomChartOptions = useKeepZoom(chartOptions, chartDateRange);
  const allDates = useMemo(() => getAllDates(dateRangeOptions), [dateRangeOptions]);
  const [series, setSeries] = useState<Array<TransactionSeries>>(
    buildSeries({ transactions, dateRange: chartDateRange, dates: allDates, t }),
  );

  useEffect(() => {
    const series = buildSeries({ transactions, dateRange: chartDateRange, dates: allDates, t });
    setSeries(series);
  }, [transactions, t, chartDateRange, allDates]);

  const presentSeriesDefinitions = getPresentSeriesDefinitions(transactions);
  const colors = presentSeriesDefinitions.map((definition) => definition.color);
  const updatedChartOptions: ApexOptions = {
    ...zoomChartOptions,
    colors,
    fill: { ...zoomChartOptions.fill, colors },
  };

  return (
    <div className="h-full">
      <h3 className="text-lg leading-6 font-medium text-gray-900">{t('dashboard:transactions.title')}</h3>
      <div className="h-full min-h-[350px]">
        <Chart options={updatedChartOptions} series={series} type="area" height="100%" />
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {presentSeriesDefinitions.map((definition) => (
              <Badge key={definition.key} color={definition.badgeColor} type="indicator">
                {t(definition.labelKey)}
              </Badge>
            ))}
          </div>
          <Link to={TransactionsRoutes.root}>
            <Button
              variant="minimal-emphasized"
              size="extra-small"
              RightIcon={MagnifyingGlassIcon}
              analyticsId="dashboard:transactions_chart_view_all"
            >
              {t('common:viewAll')}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}

// A series is only rendered when the response carries its key, so a message
// type the partner does not have never appears in the chart or its legend.
const getPresentSeriesDefinitions = (transactions?: TransactionGraphData) =>
  SERIES_DEFINITIONS.filter((definition) => transactions?.[definition.key] !== undefined);

const buildSeries = ({ transactions, dateRange, dates, t }: DefaultSeriesProps) =>
  getPresentSeriesDefinitions(transactions).map((definition) =>
    convertToSeries({ data: transactions?.[definition.key] ?? [], name: t(definition.nameKey) }, dateRange, dates),
  );

const useKeepZoom = (chartOptions: ApexOptions, chartDateRange: TransactionGraphProps['chartDateRange']) => {
  const [lastZoom, setLastZoom] = useState<Array<number>>([]);
  const updatedChartOptions = useMemo(() => {
    return {
      ...chartOptions,
      chart: {
        ...chartOptions.chart,
        events: {
          beforeResetZoom: function () {
            setLastZoom([]);
          },
          zoomed: function (_: any, value: any) {
            setLastZoom([value.xaxis.min, value.xaxis.max]);
          },
        },
      },
      xaxis: {
        ...chartOptions.xaxis,
        min: lastZoom ? lastZoom[0] : undefined,
        max: lastZoom ? lastZoom[1] : undefined,
      },
    };
  }, [lastZoom]);

  useEffect(() => {
    setLastZoom([]);
  }, [chartDateRange]);

  return updatedChartOptions;
};

/**
 * i18n keys
 * t('dashboard:orders')
 * t('dashboard:shippingNotices')
 * t('dashboard:transactions.orders')
 * t('dashboard:transactions.shippingNotices')
 * t('dashboard:transactions.invoices')
 * t('dashboard:transactions.dispatchInstructions')
 * t('dashboard:transactions.dispatchInstructionResponses')
 */
